/**
 * OaSIS Explorer Frontend - Occupation Constants
 * 
 * Shared constants for TEER levels and broad categories
 * Used by filter chips, tags, and other components
 */

import React from 'react';
import { Briefcase, GraduationCap, School, Wrench, BookOpen, Hammer } from 'lucide-react';
import { HIERARCHY_CATEGORIES } from '../hooks/useAdvancedSearch';

/**
 * TEER level short descriptions (for filter chips and tags)
 */
export const TEER_DESCRIPTIONS: Record<number, string> = {
  0: 'Management',
  1: 'University degree',
  2: 'College/Apprenticeship 2+ years',
  3: 'College/Apprenticeship <2 years',
  4: 'Secondary school',
  5: 'No formal education',
};

/**
 * TEER level full descriptions (for modal detail views)
 * These match the descriptions used in the measurement/search page filters
 */
export const TEER_DESCRIPTIONS_FULL: Record<string, string> = {
  '0': 'Management occupations',
  '1': 'Occupations usually require a university degree',
  '2': 'Occupations usually require a college diploma or apprenticeship training of two or more years; or supervisory occupations',
  '3': 'Occupations usually require a college diploma or apprenticeship training of less than two years; or more than six months of on-the-job training',
  '4': 'Occupations usually require a secondary school diploma; or several weeks of on-the-job training',
  '5': 'Occupations usually require short-term work demonstration and no formal education',
};

/**
 * Broad Category descriptions (for modal detail views)
 * These are the official titles for each of the 10 broad occupational categories
 */
export const BROAD_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  '0': 'Legislative and senior management occupations',
  '1': 'Business, finance and administration occupations',
  '2': 'Natural and applied sciences and related occupations',
  '3': 'Health occupations',
  '4': 'Occupations in education, law and social, community and government services',
  '5': 'Occupations in art, culture, recreation and sport',
  '6': 'Sales and service occupations',
  '7': 'Trades, transport and equipment operators and related occupations',
  '8': 'Natural resources, agriculture and related production occupations',
  '9': 'Occupations in manufacturing and utilities',
};

/**
 * Get TEER description by level (short version)
 */
export const getTeerDescription = (level: number): string => {
  return TEER_DESCRIPTIONS[level] || `TEER ${level}`;
};

/**
 * Get TEER description with level number for filter chips
 * e.g., "University degree (TEER 1)"
 */
export const getTeerDescriptionWithLevel = (level: number): string => {
  const description = getTeerDescription(level);
  return `${description} (TEER ${level})`;
};

/**
 * Get full TEER description by level code (for modal detail views)
 * @param code - TEER level code as string (e.g., "0", "1") or number
 * @returns Full description string or null if not found
 */
export const getTeerDescriptionFull = (code: string | number): string | null => {
  const codeStr = String(code);
  return TEER_DESCRIPTIONS_FULL[codeStr] || null;
};

/**
 * Get Broad Category description by code (for modal detail views)
 * @param code - Broad Category code as string (e.g., "0", "1") or the first digit of a multi-digit code
 * @returns Full description string or null if not found
 */
export const getBroadCategoryDescription = (code: string | number): string | null => {
  // Extract first digit from code if it's multi-digit (e.g., "12" -> "1")
  const codeStr = String(code);
  const broadCategoryCode = codeStr[0];
  return BROAD_CATEGORY_DESCRIPTIONS[broadCategoryCode] || null;
};

/**
 * Get TEER icon by level code (0-5)
 * @param code - TEER level code (0-5)
 * @param iconSize - Icon size (default: 12)
 * @returns React node with icon, or null
 */
export const getTeerIcon = (code: string | number, iconSize: number = 12): React.ReactNode | null => {
  const iconProps = { 
    size: iconSize, 
    className: "text-gray-500 dark:text-gray-400",
    strokeWidth: 2 
  };
  
  const codeStr = String(code);
  
  const iconMap: Record<string, React.ReactNode> = {
    '0': <Briefcase {...iconProps} />, // Management occupations
    '1': <GraduationCap {...iconProps} />, // University degree
    '2': <School {...iconProps} />, // College/Apprenticeship 2+ years
    '3': <Wrench {...iconProps} />, // College/Apprenticeship <2 years
    '4': <BookOpen {...iconProps} />, // Secondary school
    '5': <Hammer {...iconProps} />, // No formal education
  };
  
  return iconMap[codeStr] || null;
};

/**
 * Broad category labels (from HIERARCHY_CATEGORIES)
 */
export const BROAD_CATEGORY_LABELS = HIERARCHY_CATEGORIES.reduce((acc, cat) => {
  acc[cat.value] = cat.label;
  return acc;
}, {} as Record<string, string>);

/**
 * Get broad category label by code
 */
export const getBroadCategoryLabel = (code: string): string => {
  return BROAD_CATEGORY_LABELS[code] || code;
};

// Re-export getBroadCategoryIcon from occupationUtils to maintain consistency
export { getBroadCategoryIcon } from './occupationUtils';

